
import React from 'react';
import { AlertCircle, Clock, ExternalLink, ShieldAlert, BellRing } from 'lucide-react';
import { Alert, AlertSeverity, AlertType } from '../types';

interface AlertsSectionProps {
  alerts: Alert[];
}

export const AlertsSection: React.FC<AlertsSectionProps> = ({ alerts }) => {
  const getSeverityStyles = (severity: AlertSeverity) => {
    switch (severity) {
      case AlertSeverity.CRITICAL:
        return { bg: 'bg-rose-50 dark:bg-rose-900/20', text: 'text-rose-700 dark:text-rose-400', badge: 'bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-300' };
      case AlertSeverity.HIGH:
        return { bg: 'bg-orange-50 dark:bg-orange-900/20', text: 'text-orange-700 dark:text-orange-400', badge: 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300' };
      case AlertSeverity.MEDIUM:
        return { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-400', badge: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300' };
      case AlertSeverity.INFO:
        return { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-400', badge: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300' };
      default:
        return { bg: 'bg-slate-50 dark:bg-slate-800/20', text: 'text-slate-700 dark:text-slate-400', badge: 'bg-slate-100 dark:bg-slate-800/40 text-slate-800 dark:text-slate-300' };
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden mt-8">
      <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-400 rounded-lg">
            <ShieldAlert size={20} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">Critical Actions & Reminders</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">System detected {alerts.length} priority items needing review.</p>
          </div>
        </div>
        <button className="text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 flex items-center space-x-1">
          <span>View history</span>
          <ExternalLink size={14} />
        </button>
      </div>

      <div className="divide-y divide-slate-100 dark:divide-slate-800">
        {alerts.map((alert) => {
          const styles = getSeverityStyles(alert.severity);
          const isReminder = alert.type === AlertType.LEI_RENEWAL_REMINDER;
          
          return (
            <div key={alert.id} className={`p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors flex items-center justify-between group`}>
              <div className="flex items-start space-x-4">
                <div className={`mt-1 p-2 rounded-full ${styles.bg} ${styles.text}`}>
                  {isReminder ? <BellRing size={18} /> : <AlertCircle size={18} />}
                </div>
                <div>
                  <div className="flex items-center space-x-3 mb-1">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${styles.badge}`}>
                      {alert.severity}
                    </span>
                    <span className="text-sm font-semibold text-slate-900 dark:text-white">{alert.type}</span>
                    <span className="text-xs text-slate-400 dark:text-slate-500">• {alert.entity}</span>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">{alert.description}</p>
                  <div className="flex items-center space-x-4 text-xs text-slate-400 dark:text-slate-500">
                    <span className="flex items-center space-x-1">
                      <Clock size={12} />
                      <span>{alert.date}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <span className="w-1.5 h-1.5 bg-slate-300 dark:bg-slate-600 rounded-full"></span>
                      <span>ID: {alert.id.split('-')[0]}</span>
                    </span>
                  </div>
                </div>
              </div>
              
              <button className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                isReminder 
                ? 'bg-blue-600 text-white hover:bg-blue-700 border-blue-600' 
                : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-900 dark:hover:bg-white hover:text-white dark:hover:text-slate-900 hover:border-slate-900 dark:hover:border-white'
              }`}>
                {isReminder ? 'Renew' : 'Review'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
